import styles from "./BunchPage.module.scss";
import Box from "@components/Box";
import DataTable from "@containers/DataTable";
import DocumentLink from '@components/DocumentLink';
import EditBunchModal from "./EditBunchModal";
import InfoIcon from '@assets/icons/info_blue_16.svg';
import Page from "@components/Page";
import PlusIcon from "@assets/icons/plus.svg";
import { Button, ButtonSize, ButtonType } from "@components/Button";
import { DataViewHandle } from "@containers/DataTable";
import { FilterModel } from "@hooks/useFiltersSearchParams";
import { Filters, FilterTypes } from "@containers/Filters";
import { getBunchList, BunchItem, } from '@api/apiClient/bunch';
import { getCurrentLanguage, Langs } from '@utils/lang';
import { Row, Column } from "@components/Grid";
import { TableColumn, TableColumnType, TableRowType, } from "@components/Table/TableTypes";
import { Text, TextType, TextSize } from "@components/Text";
import { useState, useRef } from 'react';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from "react-i18next";

type TLinkData = {
  fileSize: string;
  url: string;
}

const urlPrefix = 'https://storage.yandexcloud.net/public-for-business/Instructions/';
const instruction: Record<Langs, TLinkData | null> = {
  [Langs.cn]: {
    fileSize: '500 KB',
    url: `${urlPrefix}CN/%E8%B7%A8%E5%BA%97%E9%93%BA%E5%A6%82%E4%BD%95%E8%BD%AC%E5%94%AE%E5%8C%85%E8%A3%B9.pdf`
  },
  [Langs.en]: null,
  [Langs.ru]: null,
  [Langs.tr]: null
};

const BunchPage = () => {
  const [activeBunch, setActiveBunch] = useState<BunchItem | null>(null);
  const [filters, setFilters] = useState<FilterModel[]>([]);
  const [isEditBunchOpen, setEditBunchOpen] = useState<boolean>(false);

  const dataRef = useRef<DataViewHandle>(null);

  const currentLanguage = getCurrentLanguage();
  const { account } = useProfile();
  const { t } = useTranslation();

  const columns: TableColumn<BunchItem>[] = [
    {
      header: t("bunchPage.product"),
      accessor: (row: TableRowType<BunchItem>) => (
        <span
          className={styles.product}
        >
          {row.data.name}
        </span>
      ),
      alignment: "left",
      width: "425px"
    },
    {
      header: t("bunchPage.count"),
      accessor: (row: TableRowType<BunchItem>) => row.data.vendorCodeValues.length,
      type: TableColumnType.TERTIARY,
      alignment: "right",
      width: "auto"
    },
    {
      header: t("bunchPage.articles"),
      accessor: (row: TableRowType<BunchItem>) => row.data.vendorCodeValues.join(", "),
      alignment: "left",
      width: "425px"
    },
  ];

  return (
    <Page
      title={t("bunchPage.title")}
      actions={(
        <>
          <Filters
            items={[
              {
                name: "SearchWords",
                type: FilterTypes.MULTI_SEARCH_OR_NAME,
                placeholder: t("bunchPage.trackNumberSearch"),
              },
            ]}
            onChange={(v) => setFilters(v)}
            values={filters}
          />
          <Button
            icon={PlusIcon}
            onClick={() => {
              setActiveBunch(null);
              setEditBunchOpen(true);
            }}
            size={ButtonSize.S}
            type={ButtonType.ROUNDED}
            title={t("bunchPage.createBunch")}
          />
        </>
      )}
    >
      {account?.region === 'CN' && (
          <Box pb={5}>
            <Row gap={6}>
              <DocumentLink
                  icon={InfoIcon}
                  text={t('bunchPage.downloadInstruction')}
                  {...(instruction[currentLanguage] || instruction[Langs.cn]) as TLinkData}
              />
            </Row>
          </Box>
      )}
      <Box pb={5}>
        <Row>
          <Column phone={12}>
            <Text
                size={TextSize.S}
                type={TextType.BLOCK}
            >
              {t("bunchPage.description")}
            </Text>
          </Column>
        </Row>
      </Box>

      <DataTable<BunchItem>
        queryKey="bunch-list"
        getData={getBunchList}
        columns={columns}
        notFound={t("bunchPage.notReturns")}
        filters={filters}
        dataRef={dataRef}
        rowOnClick={(data) => {
          setActiveBunch(data);
          setEditBunchOpen(true);
        }}
        className={styles.table}
      />

      {isEditBunchOpen ? (
        <EditBunchModal
          data={activeBunch}
          onClose={() => setEditBunchOpen(false)}
          onSuccess={() => {
            if (typeof dataRef.current?.refresh === "function") {
              dataRef.current?.refresh();
            }
            setEditBunchOpen(false);
          }}
        />
      ) : null}
    </Page>
  );
};

export default BunchPage;